import React, { useState, useEffect, useCallback } from "react";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Pencil, ChefHat, X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const emptyIng = () => ({ product: "", quantity: 1, unit: "pz" });
const empty = () => ({ name: "", servings: 4, ingredients: [emptyIng()], notes: "" });

export default function Ricette() {
  const { canWrite } = useAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scaleById, setScaleById] = useState({});
  const getScale = (r) => { const v = scaleById[r.id]; return v == null || v === "" ? r.servings : Number(v); };
  const setScaleFor = (r, v) => setScaleById((s) => ({ ...s, [r.id]: Math.max(1, Number(v) || 1) }));

  const load = useCallback(() => api.get("/recipes").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setIng = (i, patch) => setForm({ ...form, ingredients: form.ingredients.map((x, idx) => idx === i ? { ...x, ...patch } : x) });
  const addIng = () => setForm({ ...form, ingredients: [...form.ingredients, emptyIng()] });
  const rmIng = (i) => setForm({ ...form, ingredients: form.ingredients.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome della ricetta");
    setSaving(true);
    try {
      await api.post("/recipes", { ...form, servings: Number(form.servings) || 1, ingredients: form.ingredients.map((i) => ({ ...i, quantity: Number(i.quantity) })) });
      toast.success("Ricetta salvata");
      setForm(null);
      load();
    } catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa ricetta?")) return;
    await api.delete(`/recipes/${id}`);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Ricette</h1>
          <p className="text-slate-500">Costo per piatto e per porzione, calcolato sui prezzi reali degli acquisti</p>
        </div>
        {canWrite && (
          <button data-testid="add-recipe-btn" onClick={() => setForm(empty())} className="flex h-14 items-center gap-2 rounded-xl bg-navy px-5 font-semibold text-white hover:bg-navy-hover">
            <Plus className="h-5 w-5" /> Nuova ricetta
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <ChefHat className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-400">Nessuna ricetta. Creane una per calcolarne il food cost.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((r) => (
            <div key={r.id} data-testid={`recipe-${r.id}`} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between">
                <div className="font-head text-lg font-semibold text-navy">{r.name}</div>
                {canWrite && (
                  <div className="flex gap-1">
                    <button onClick={() => setForm(r)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-recipe-${r.id}`} onClick={() => remove(r.id)} className="rounded p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              <div className="mt-1 text-sm text-slate-500">{r.servings} porzioni · {r.ingredients?.length || 0} ingredienti</div>
              <div className="mt-3 flex items-end justify-between border-t border-slate-100 pt-3">
                <div>
                  <div className="text-xs text-slate-400">Costo piatto</div>
                  <div className="tnum font-head text-lg font-bold text-navy">{eur(r.cost_total)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">Costo / porzione</div>
                  <div data-testid={`recipe-cps-${r.id}`} className="tnum font-head text-xl font-bold text-gold">{eur(r.cost_per_serving)}</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-xs font-medium text-slate-500">Scala per ospiti</span>
                <div className="flex items-center gap-1.5">
                  <button data-testid={`scale-minus-${r.id}`} onClick={() => setScaleFor(r, getScale(r) - 1)} className="h-7 w-7 rounded bg-white font-bold text-navy shadow-sm">−</button>
                  <input data-testid={`scale-input-${r.id}`} type="number" value={getScale(r)} onChange={(e) => setScaleFor(r, e.target.value)} className="tnum h-8 w-12 rounded border border-slate-200 text-center text-sm outline-none focus:border-gold" />
                  <button data-testid={`scale-plus-${r.id}`} onClick={() => setScaleFor(r, getScale(r) + 1)} className="h-7 w-7 rounded bg-white font-bold text-navy shadow-sm">+</button>
                </div>
                <span data-testid={`scale-cost-${r.id}`} className="tnum text-sm font-bold text-navy">{eur(r.cost_per_serving * Math.max(1, getScale(r)))}</span>
              </div>
              {r.has_missing_prices && (
                <div className="mt-2 flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> Alcuni ingredienti senza prezzo negli acquisti</div>
              )}
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="animate-slide-up flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl bg-white sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 p-5">
              <h3 className="font-head text-xl font-bold text-navy">{form.id ? "Modifica" : "Nuova"} ricetta</h3>
              <button onClick={() => setForm(null)}><X className="h-6 w-6 text-slate-400" /></button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="mb-1 block text-sm font-semibold text-navy">Nome piatto *</label>
                  <input data-testid="recipe-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-navy">Porzioni</label>
                  <input data-testid="recipe-servings" type="number" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 text-center outline-none focus:border-gold" />
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-navy">Ingredienti</label>
                  <button data-testid="add-ingredient" onClick={addIng} className="flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium hover:bg-slate-200"><Plus className="h-4 w-4" /> Riga</button>
                </div>
                <div className="space-y-2">
                  {form.ingredients.map((ing, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <input placeholder="Ingrediente (come negli acquisti)" value={ing.product} onChange={(e) => setIng(i, { product: e.target.value })} className="col-span-6 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" data-testid={`ing-product-${i}`} />
                      <input type="number" placeholder="Qtà" value={ing.quantity} onChange={(e) => setIng(i, { quantity: e.target.value })} className="col-span-3 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" data-testid={`ing-qty-${i}`} />
                      <input placeholder="Un." value={ing.unit} onChange={(e) => setIng(i, { unit: e.target.value })} className="col-span-2 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" />
                      <button onClick={() => rmIng(i)} className="col-span-1 flex items-center justify-center rounded text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-400">Il costo usa il prezzo unitario più recente dello stesso prodotto negli Acquisti.</p>
              </div>
            </div>
            <div className="border-t border-slate-100 p-5">
              <button data-testid="save-recipe-btn" onClick={save} disabled={saving} className="flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-gold font-bold text-black hover:bg-gold-hover disabled:opacity-60">
                {saving && <Loader2 className="h-5 w-5 animate-spin" />} Salva ricetta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
